// @flow
import Debug from 'debug';
import type { State, Target } from '../types';
import { reverseCoordinates, degToRad, radToDeg, isValue } from '../utils';
import { getDistance } from '../utils/targetUtils';
import { debugConfigSelector } from './configSelectors';

const rtDebug = Debug('rt:amms:selectors:target');

// Heading computation function will return heading 0 if change in position is less than this in meters
const HEADING_MOVEMENT_THRESHOLD = 5;
const HEADING_TIME_THRESHOLD = 5000;
const HEADING_BUFFER_THRESHOLD = 5;

export const vehiclesSubStateSelector = (state: State) => state.vehicles;

export const vehicleEntitiesSelector = (state: State) => {
    const subState = vehiclesSubStateSelector(state);
    return subState.entities;
};

export const makeVehicleEntitySelector = (icao: string) => (state: State) => {
    const entities = vehicleEntitiesSelector(state);
    return entities[icao];
};

export const convertCoordinates = (coordinates: Coordinates): Coordinates => {
    const reveresed = reverseCoordinates(coordinates);
    return [degToRad(reveresed[0]), degToRad(reveresed[1])];
};
export const headingComputedSelector = (targetBuffer?: $ReadOnlyArray<Target>) => (state: State) => {
    const debugConfig = debugConfigSelector(state);
    const target = targetBuffer && targetBuffer[0];

    return Boolean(!target || (debugConfig && debugConfig.discardHeadingData) || !target.MovementData || !isValue(target.MovementData.GroundTrack));
};
export const headingSelector = (icao: string, targetBuffer?: $ReadOnlyArray<Target>) => (state: State) => {
    const target = targetBuffer && targetBuffer[0];

    if(target) {
        const computeHeading = headingComputedSelector(targetBuffer)(state);
        if(!computeHeading) {
            return target.MovementData.GroundTrack;
        }
        else {
            const targets = [];

            for(let i = 0, length = targetBuffer.length; i < length; i += 1) {
                const target = targetBuffer[i];

                if(target.MovementData && target.MovementData.Position) {
                    const previousTarget = i > 0 && targets[i - 1];
                    if(!previousTarget || target.TimeOfTrackInformation !== previousTarget.TimeOfTrackInformation) {
                        targets.push(target);
                    }
                }

                if(targets.length === 2) {
                    break;
                }
            }

            if(targets.length !== 2) {
                return undefined;
            }

            if(HEADING_MOVEMENT_THRESHOLD) {
                const firstMessage = targetBuffer[0];
                const latestDate = new Date(firstMessage.TimeOfTrackInformation);

                let secondMessage;
                for(let i = 1, length = targetBuffer.length; i < length; i += 1) {
                    const target = targetBuffer[i];

                    if(latestDate - new Date(target.TimeOfTrackInformation) >= HEADING_TIME_THRESHOLD) {
                        secondMessage = target;
                        break;
                    }
                }

                if(!secondMessage && targetBuffer.length >= HEADING_BUFFER_THRESHOLD) {
                    secondMessage = targetBuffer[targetBuffer.length - 1];
                }

                if(secondMessage) {
                    const distance = getDistance(firstMessage.MovementData.Position, secondMessage.MovementData.Position);

                    if(distance <= HEADING_MOVEMENT_THRESHOLD) {
                        if(distance > 0) {
                            rtDebug(`Setting computed heading to 0 because target moved only ${distance}m. Threshold is ${HEADING_MOVEMENT_THRESHOLD}m. Icao: ${icao}`);
                        }

                        return 0;
                    }
                }
            }

            const coords1 = convertCoordinates(targets[0].MovementData.Position);
            const coords2 = convertCoordinates(targets[1].MovementData.Position);
            const lon1 = coords1[0];
            const lon2 = coords2[0];
            const lat1 = coords1[1];
            const lat2 = coords2[1];

            const a = Math.sin(lon2 - lon1) * Math.cos(lat2);
            const b = Math.cos(lat1) * Math.sin(lat2) -
                Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);

            return (radToDeg(Math.atan2(a, b)) + 180) % 360;
        }
    }
    else {
        return undefined;
    }
};

export const speedComputedSelector = (targetBuffer?: $ReadOnlyArray<Target>) => (state: State) => {
    const debugConfig = debugConfigSelector(state);
    const target = targetBuffer && targetBuffer[0];
    const speed = target && target.MovementData && target.MovementData.GroundSpeed;

    return Boolean(!target || (debugConfig && debugConfig.discardSpeedData) || !isValue(speed));
};
export const speedSelector = (icao: string, targetBuffer?: $ReadOnlyArray<Target>) => (state: State) => {
    const target = targetBuffer && targetBuffer[0];

    if(target) {
        const computeSpeed = speedComputedSelector(targetBuffer)(state);
        const speed = target.MovementData && target.MovementData.GroundSpeed;

        if(!computeSpeed) {
            return speed;
        }
        else {
            const targets = [];

            for(let i = 0, length = targetBuffer.length; i < length; i += 1) {
                const target = targetBuffer[i];

                if(target.TimeOfTrackInformation && target.MovementData && target.MovementData.Position) {
                    const previousTarget = i > 0 && targets[i - 1];
                    if(!previousTarget || target.TimeOfTrackInformation !== previousTarget.TimeOfTrackInformation) {
                        targets.push(target);
                    }
                }

                if(targets.length === 2) {
                    break;
                }
            }

            if(targets.length !== 2) {
                return undefined;
            }

            const distance = getDistance(targets[0].MovementData.Position, targets[1].MovementData.Position);
            const time = Math.abs(new Date(targets[0].TimeOfTrackInformation) - new Date(targets[1].TimeOfTrackInformation));

            if(time === 0) {
                if(distance > 0) {
                    rtDebug(`Setting computed speed to 0 because target moved ${distance}m but in no time difference. Icao: ${icao}`);
                }

                return 0;
            }
            else {
                return distance / (time / 3600);
            }
        }
    }
    else {
        return undefined;
    }
};